import type { HelpGuide } from '../types';

export const prepaidGuide: HelpGuide = {
  key: 'prepaid',
  title: 'ค่าใช้จ่ายจ่ายล่วงหน้า (Prepaid Expenses)',
  subtitle: 'เมนู: บัญชีแยกประเภท ▸ ค่าใช้จ่ายจ่ายล่วงหน้า',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/prepaid/คู่มือการใช้งาน_ค่าใช้จ่ายจ่ายล่วงหน้า_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ค่าใช้จ่ายจ่ายล่วงหน้า” (Prepaid Expenses) ใช้คุมสัญญาที่จ่ายเงินไปก่อนแล้วทยอยรับรู้เป็นค่าใช้จ่ายตามอายุสัญญา เช่น เบี้ยประกัน ค่าเช่า License ซอฟต์แวร์ สัญญาบำรุงรักษา ค่าโฆษณา ฯลฯ ระบบจะตั้งเป็นสินทรัพย์ (บัญชี 10190001 — ค่าใช้จ่ายจ่ายล่วงหน้า) แล้ว “ตัดจ่าย” (amortize) เข้าค่าใช้จ่ายทุกเดือนให้อัตโนมัติ',
        },
        {
          kind: 'callout',
          text: 'แนวคิด: จ่ายเงินครั้งเดียว → ตั้งเป็นสินทรัพย์ → ทยอยตัดเป็นค่าใช้จ่ายรายเดือนตามอายุสัญญา (Dr ค่าใช้จ่าย / Cr 10190001) จนครบ',
        },
        { kind: 'para', text: 'หน้าจอมี 5 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ทะเบียนสัญญา — ',
              text: 'รายการสัญญาทั้งหมด + ตัวกรอง + ยอดสรุป (แท็บเริ่มต้น)',
            },
            { lead: 'ตารางตัดจ่ายรายเดือน — ', text: 'ตารางตัดจ่ายทีละงวดของสัญญาที่เลือก' },
            {
              lead: 'ประวัติการตัดจ่าย (JV) — ',
              text: 'รอบประมวลผล (อนุมัติ/กลับรายการ) + ใบสำคัญที่ผ่านรายการแล้ว',
            },
            { lead: 'สรุปตามหมวด — ', text: 'แยกตามหมวด + พยากรณ์ 12 เดือน + กระทบยอดยอดยกมา' },
            { lead: 'กระทบยอด & Roll-forward — ', text: 'กระทบยอดทะเบียนกับบัญชี 10190001 ใน GL' },
          ],
        },
        {
          kind: 'image',
          src: '/help/prepaid/01-register.png',
          caption:
            'ภาพรวมหน้าค่าใช้จ่ายจ่ายล่วงหน้า — การ์ด KPI, แถบประมวลผล, ตัวกรอง และตารางทะเบียนสัญญา',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) ด้านบน 5 ใบ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ยอดคงเหลือรวม (10190001) — ',
              text: 'ยอดสินทรัพย์ค่าใช้จ่ายล่วงหน้าที่ยังตัดไม่หมด',
            },
            { lead: 'ตัดจ่ายเดือนนี้ — ', text: 'ยอดที่จะตัดเป็นค่าใช้จ่ายในงวดปัจจุบัน' },
            { lead: 'มูลค่าสัญญารวม — ', text: 'มูลค่ารวมของทุกสัญญา' },
            { lead: 'สัญญาใหม่เริ่ม — ', text: 'จำนวนสัญญาที่เริ่มในเดือนนี้' },
            { lead: 'ใกล้ครบ (90 วัน) — ', text: 'สัญญาที่ใกล้สิ้นสุดภายใน 90 วัน' },
          ],
        },
        { kind: 'para', text: 'ปุ่มคำสั่งมุมขวาบน:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'รันตัดจ่ายเดือน — ', text: 'เปิดหน้าต่างประมวลผลตัดจ่ายของงวด' },
            {
              lead: 'นำเข้า / นำเข้ายอดยกมา / นำเข้าแก้ไข — ',
              text: 'นำเข้าสัญญาใหม่ / ยอดยกมา (Go-live) / แก้ไขเป็นชุดจาก Excel',
            },
            { lead: 'ส่งออก — ', text: 'ส่งออกทะเบียนสัญญาเป็น Excel' },
            { lead: 'ลงทะเบียนใหม่ — ', text: 'เปิดฟอร์มลงทะเบียนสัญญาใหม่ (ปุ่มสีแดง)' },
          ],
        },
        {
          kind: 'para',
          text: 'ตารางทะเบียนกรองได้ด้วย ค้นหา/หมวด/สถานะ/สาขา/หน่วยงาน/ช่วงวันที่ และบันทึก “มุมมอง” (saved views) ได้ คอลัมน์: เลขที่ · ผู้ขาย/รายละเอียด · หมวด · มูลค่าสัญญา · งวดตัดจ่าย · ตัดจ่าย/เดือน · เริ่ม-สิ้นสุด · ความคืบหน้า · คงเหลือ · สถานะ — คลิกที่แถวเพื่อดูรายละเอียด',
        },
      ],
    },
    {
      id: 'add',
      heading: '3. การลงทะเบียนสัญญาใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม ลงทะเบียนใหม่ แล้วกรอก: หมวดค่าใช้จ่าย (เลือกการ์ดหมวด → ผูกบัญชีค่าใช้จ่ายให้) · ผู้ขาย/คู่สัญญา · อ้างอิงใบสำคัญจ่าย · รายละเอียดสัญญา · มูลค่าสัญญา ÷ จำนวนงวด (ระบบคำนวณ “ตัดจ่ายต่อเดือน” ให้) · วันที่จ่ายเงิน · เริ่มตัดจ่ายงวดแรก',
        },
        {
          kind: 'para',
          text: 'เลือกวิธีตัดจ่าย: เฉลี่ยเท่ากันทุกงวด หรือ เฉลี่ยตามจำนวนวัน — ระบบจะแสดง “จำลองตารางตัดจ่าย” (what-if) เทียบทั้งสองวิธีแบบเรียลไทม์ และมีตัวเลือกเปิดการตัดจ่ายอัตโนมัติทุกสิ้นเดือน',
        },
        {
          kind: 'callout',
          text: 'ถ้ามูลค่าต่ำกว่าเกณฑ์ความมีนัยสำคัญ ระบบจะเสนอให้ “ลงเป็นค่าใช้จ่ายงวดเดียว” แทนการตั้งเป็นสินทรัพย์ตัดจ่าย',
        },
        {
          kind: 'image',
          src: '/help/prepaid/08-add.png',
          caption:
            'ฟอร์มลงทะเบียนใหม่ — เลือกหมวด, มูลค่า ÷ งวด = ตัดจ่าย/เดือน และจำลองตารางตัดจ่าย (เฉลี่ยเท่ากัน vs ตามจำนวนวัน)',
        },
      ],
    },
    {
      id: 'schedule',
      heading: '4. ตารางตัดจ่ายรายเดือน',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ตารางตัดจ่ายรายเดือน (หรือคลิกแถวในทะเบียน) แสดงตารางตัดจ่ายทีละงวดของสัญญา: งวด · เดือน · วันที่ตัดจ่าย · เลขที่ JV · เดบิต · เครดิต · จำนวนเงิน · คงเหลือยังไม่ตัด · สถานะ (ผ่านรายการแล้ว / งวดปัจจุบัน / รอประมวลผล) พร้อมปุ่มพิมพ์ใบรับรองและส่งออก Excel',
        },
        {
          kind: 'image',
          src: '/help/prepaid/03-schedule.png',
          caption: 'ตารางตัดจ่ายรายเดือน — แต่ละงวดแสดงจำนวนตัดจ่าย ยอดคงเหลือ เลขที่ JV และสถานะ',
        },
      ],
    },
    {
      id: 'run',
      heading: '5. การประมวลผลตัดจ่าย (รันตัดจ่าย)',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม รันตัดจ่ายเดือน (หรือ ตรวจสอบ & ประมวลผล จากแถบด้านบน) ระบบเปิดหน้าต่างประมวลผล — เลือกช่วงงวด (ตัดย้อนหลายงวดได้) แสดงรายการสัญญาที่จะตัดจ่ายพร้อมยอด ติ๊กเลือก/ไม่เลือกรายได้ แล้วกด ดู JV ก่อนรัน หรือ ประมวลผล & ลง JV',
        },
        {
          kind: 'image',
          src: '/help/prepaid/04-run.png',
          caption:
            'หน้าต่างประมวลผลตัดจ่าย — ช่วงงวด, รายการสัญญาที่จะตัด (เลือกได้), ยอดรวม และปุ่มลง JV',
        },
        {
          kind: 'para',
          text: 'รอบประมวลผลเข้าสู่การอนุมัติแบบสี่ตา ดูได้ที่แท็บ ประวัติการตัดจ่าย (JV): ตารางรอบประมวลผลแสดงสถานะ รออนุมัติ / ผ่านอนุมัติ / กลับรายการ พร้อมสายอนุมัติ (พิจารณา) และปุ่ม กลับรายการ (ออก JV กลับรายการในงวดเปิดถัดไป) ด้านล่างคือใบสำคัญ (JV Batch) ที่ผ่านรายการแล้ว',
        },
        {
          kind: 'image',
          src: '/help/prepaid/05-journal.png',
          caption:
            'แท็บประวัติการตัดจ่าย — รอบประมวลผล (อนุมัติ/กลับรายการ) และใบสำคัญที่ผ่านรายการแล้ว',
        },
      ],
    },
    {
      id: 'lifecycle',
      heading: '6. รายละเอียดสัญญา และการปรับปรุง (Lifecycle)',
      blocks: [
        {
          kind: 'para',
          text: 'คลิกที่แถวในทะเบียนเพื่อเปิดแผงรายละเอียด (เลื่อนจากขวา) แสดง KPI ของสัญญา (มูลค่า/ตัดจ่ายต่อเดือน/คงเหลือ/ช่วงตัดจ่าย/JV ตั้งต้น) พร้อมแท็บย่อย: ตารางตัดจ่าย · ประวัติการแก้ไข · เอกสารหลักฐาน',
        },
        {
          kind: 'image',
          src: '/help/prepaid/02-detail.png',
          caption:
            'แผงรายละเอียดสัญญา — KPI, ปุ่มปรับปรุงวงจรชีวิต และตารางตัดจ่าย/ประวัติ/เอกสารหลักฐาน',
        },
        { kind: 'para', text: 'ปุ่มปรับปรุงวงจรชีวิต (เฉพาะสถานะที่ทำได้):' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ปรับปรุงตาราง — ',
              text: 'แก้มูลค่า/วันสิ้นสุด แล้วกระจายยอดคงเหลือใหม่ตามอายุที่เหลือ (งวดที่ลงแล้วคงเดิม)',
            },
            {
              lead: 'พักสัญญา — ',
              text: 'หยุดตัดจ่ายชั่วคราว (ไม่มี JV) เปิดใช้ใหม่แล้วตัดย้อนงวดที่ข้ามได้',
            },
            { lead: 'เปิดใช้ใหม่ — ', text: 'กลับมาตัดจ่ายต่อ' },
            {
              lead: 'ยกเลิกสัญญา — ',
              text: 'ตัดจำหน่ายยอดคงเหลือเป็นค่าใช้จ่าย (และคืนเงินถ้ามี)',
            },
            { lead: 'คัดลอก / ต่ออายุ — ', text: 'สร้างสัญญาใหม่จากสัญญาเดิม' },
          ],
        },
      ],
    },
    {
      id: 'recon',
      heading: '7. กระทบยอด GL & Roll-forward',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ กระทบยอด & Roll-forward เลือกงวดแล้วแสดง “ไฟกระทบยอด”: ยอดคงเหลือตามทะเบียน เทียบ ยอดคงเหลือใน GL (10190001) และส่วนต่าง (Variance) ถ้าตรง = เขียว ถ้าไม่ตรง = แดง พร้อมปุ่มดูรายการที่ต้องตรวจ ใต้ลงไปคือตาราง Roll-forward ต่อสัญญา: ยอดยกมา · เพิ่มขึ้น · ตัดจ่าย · ตัดจำหน่าย · ยอดคงเหลือ',
        },
        {
          kind: 'image',
          src: '/help/prepaid/06-rollforward.png',
          caption:
            'กระทบยอด & Roll-forward — ไฟส่วนต่าง (ทะเบียน vs GL 10190001) และตารางเคลื่อนไหวยอดต่อสัญญา',
        },
        {
          kind: 'para',
          tone: 'muted',
          text: 'ประเภทรายการที่ต้องตรวจ: ยังไม่ตัดจ่าย · ไม่มีเลข JV · ยอดไม่ตรง · ยอดค้างหลังสิ้นสุด · รายการ manual ใน 10190001',
        },
      ],
    },
    {
      id: 'summary',
      heading: '8. สรุปตามหมวด & พยากรณ์',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ สรุปตามหมวด แสดงยอดคงเหลือแยกตามหมวด (เบี้ยประกัน/ค่าเช่า/License/บำรุงรักษา/โฆษณา/ค่าธรรมเนียม) พร้อมแถบสัดส่วน และกราฟ “พยากรณ์การตัดจ่ายอัตโนมัติ 12 เดือนข้างหน้า” ถ้ามีการนำเข้ายอดยกมา (Go-live) จะมีแผงกระทบยอดยอดยกมาให้ตรวจส่วนต่างด้วย',
        },
        {
          kind: 'image',
          src: '/help/prepaid/07-summary.png',
          caption:
            'สรุปตามหมวด — กระทบยอดยอดยกมา (Go-live), สัดส่วนตามหมวด และพยากรณ์ตัดจ่าย 12 เดือน',
        },
      ],
    },
    {
      id: 'import-export',
      heading: '9. การนำเข้าและส่งออกข้อมูล',
      blocks: [
        {
          kind: 'para',
          text: 'ส่งออก: ปุ่ม ส่งออก ดาวน์โหลดทะเบียนสัญญาตามตัวกรองปัจจุบันเป็น Excel นำเข้า: มี 3 แบบ — นำเข้าสัญญาใหม่ · นำเข้ายอดยกมา (Go-live take-on) · นำเข้าแก้ไขเป็นชุด แต่ละแบบมีดาวน์โหลดเทมเพลต ผังคอลัมน์ และพรีวิวก่อนสร้างจริง',
        },
        {
          kind: 'image',
          src: '/help/prepaid/09-import.png',
          caption: 'หน้าต่างนำเข้าค่าใช้จ่ายจ่ายล่วงหน้า — ผังคอลัมน์ ดาวน์โหลดเทมเพลต และพรีวิว',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '10. สรุปสถานะของสัญญา',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['กำลังตัดจ่าย', 'สัญญาที่กำลังทยอยตัดจ่ายตามตาราง'],
            ['ยังไม่เริ่ม', 'ลงทะเบียนแล้วแต่ยังไม่ถึงงวดเริ่มตัดจ่าย'],
            ['พักสัญญา', 'หยุดตัดจ่ายชั่วคราว (เปิดใช้ใหม่ได้)'],
            ['ตัดจ่ายครบ', 'ตัดจ่ายครบทุกงวดแล้ว (คงเหลือ 0)'],
            ['ยกเลิกแล้ว', 'ยกเลิกสัญญา — ตัดจำหน่ายยอดคงเหลือแล้ว'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '11. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ตัดจ่ายรายเดือนเกิดขึ้นได้อย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'กดปุ่ม รันตัดจ่ายเดือน เลือกงวด → ระบบลงใบสำคัญตัดจ่าย (เข้าอนุมัติสี่ตา) หรือเปิดตัดจ่ายอัตโนมัติทุกสิ้นเดือนตอนลงทะเบียน',
            },
          ],
        },
        { kind: 'para', text: 'ตัดจ่ายผิด/ต้องการยกเลิกรอบที่ลงไปแล้ว?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ที่แท็บ ประวัติการตัดจ่าย (JV) → ปุ่ม กลับรายการ ระบบออก JV กลับรายการในงวดเปิดถัดไป',
            },
          ],
        },
        { kind: 'para', text: 'ทะเบียนกับบัญชี 10190001 ไม่ตรงกัน?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ดูแท็บ กระทบยอด & Roll-forward หาส่วนต่าง (สีแดง) แล้วกด ดูรายการที่ต้องตรวจ',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '12. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ค่าใช้จ่ายจ่ายล่วงหน้า — ',
              text: 'เงินที่จ่ายไปก่อนสำหรับบริการที่ยังไม่ได้ใช้ ตั้งเป็นสินทรัพย์ (10190001)',
            },
            {
              lead: 'ตัดจ่าย (Amortize) — ',
              text: 'ทยอยรับรู้สินทรัพย์เป็นค่าใช้จ่ายตามอายุสัญญา',
            },
            {
              lead: 'เฉลี่ยเท่ากัน / ตามจำนวนวัน — ',
              text: 'วิธีกระจายค่าตัดจ่ายต่องวด (เท่ากันทุกงวด หรือถ่วงตามจำนวนวัน)',
            },
            {
              lead: 'Roll-forward — ',
              text: 'ตารางเคลื่อนไหวยอด (ยกมา + เพิ่ม − ตัดจ่าย − ตัดจำหน่าย = คงเหลือ)',
            },
            { lead: 'กระทบยอด (Tie-out) — ', text: 'เทียบยอดทะเบียนกับยอดใน GL (10190001)' },
            { lead: 'ยอดยกมา (Go-live) — ', text: 'ยอดสัญญาที่ตั้งต้นตอนเริ่มใช้ระบบ' },
          ],
        },
      ],
    },
  ],
};
